import { computed, ref, useFetch } from '@nuxtjs/composition-api';
import { useStore } from '~/composables/useStore';
import { WINE_INTRODUCTION_REVIEW_THRESHOLD } from '~/constants';
import { url } from '~/utils';
import { getLotteryInfoFromEC } from '~/utils/checkLotteryStatus';
import { getProductPrice, getSpecialPricePromotion, formatKeyValueArray } from '~/utils/modules/products';
import { DynamicPartsProductIntroduction } from '~/types/modules/contents';
import { ProductInfoForIntroParts, ProductShowECResponseSchema } from '~/types/modules/product';

export const useProductIntroduction = (props: { item: DynamicPartsProductIntroduction; productCode: string }) => {
  const store = useStore();
  const ecDetailResponse = ref<ProductShowECResponseSchema>();
  const searchProduct = ref<ProductInfoForIntroParts>();
  const productDetailUrl = url('ITEM_DETAIL', { code: props.productCode });

  useFetch(async () => {
    ecDetailResponse.value = await store.dispatch('product/detail/fetchECProductDetail', {
      path: {
        code: props.item.product_code,
      },
    });

    if (!ecDetailResponse.value) return;

    searchProduct.value = await store.dispatch('modules/product/fetchInfoForIntroductionParts', {
      q: `mst:product AND product_code_s: ${ecDetailResponse.value.code}`,
    });
  });

  /** 管理画面側で指定された子商品があるかどうか */
  const hasProductVariant = computed(() => !!props.item.product_variant_code);

  // newラベルを表示するかどうか
  const isShowNew = computed(() => {
    return !!searchProduct.value?.is_new;
  });

  // 生産者名
  const maker = computed(() => {
    return ecDetailResponse.value?.master_settings?.producer?.name_kana;
  });

  /** クチコミ */
  const review = computed(() => {
    const reviewSettings = ecDetailResponse.value?.review_settings;
    if (!reviewSettings) return;
    const { rating_average, review_total } = reviewSettings;
    if (rating_average < WINE_INTRODUCTION_REVIEW_THRESHOLD) return;
    return { rating_average, review_total };
  });
  // 商品名
  const productName = computed(() => {
    return ecDetailResponse.value?.langs.find((lang) => lang.id === 'ja')?.name || '';
  });

  /** 特集用キャッチ（管理画面の入力項目名）or 商品マスタに登録されてる３行キャッチ */
  const threeLineCatchCopies = computed(() => {
    const catchCopy = props.item.catch_text || ecDetailResponse.value?.three_line_catch_copy;

    return catchCopy?.split('\n') || [];
  });

  /** 説明（管理画面の入力項目名） */
  const catchCopies = computed(() => {
    return props.item.catch_copy?.split('\n') || [];
  });

  /**
   * 子商品
   * 管理画面で子商品コード未指定の場合、最安の子商品のみ取得
   * 子商品コード指定ありの場合、指定された子商品を取得（１件のみ指定可）
   */
  const productVariant = computed(() => {
    const productVariant = ecDetailResponse.value?.product_variants?.find((variant) => {
      if (!hasProductVariant.value && variant.id === ecDetailResponse.value?.best_priced_variant_id) return variant;

      return variant.code === props.item.product_variant_code;
    });

    if (!productVariant) return;

    const { code, id, stock_count, price_settings, now_on_sale_flag, attributes } = productVariant;

    const SpecialPricePromotion = getSpecialPricePromotion(price_settings);
    const formattedAttributes = formatKeyValueArray(attributes);

    return {
      productVariantCode: code,
      productVariantId: id,
      discountRate: SpecialPricePromotion?.discount_rate,
      isFreeShipping: Boolean(price_settings?.is_free_shipping),
      isLimitedStock: formattedAttributes?.mark_limited_stock_flag === 'true',
      isOnlineCampaign: formattedAttributes?.mark_limited_online_campaign_flag === 'true',
      saleLabel: price_settings?.sale_info,
      stock: stock_count || 0,
      price: getProductPrice(price_settings),
      isNowOnSale: now_on_sale_flag,
    };
  });

  const getLotteryInfo = (productVariantId: number) => {
    const lotteryInfo = ecDetailResponse.value?.product_variants.find((variant) => variant.id === productVariantId)
      ?.raffle_item?.raffle;

    return getLotteryInfoFromEC(lotteryInfo);
  };

  return {
    url,
    ecDetailResponse,
    productVariant,
    maker,
    productName,
    searchProduct,
    isShowNew,
    catchCopies,
    threeLineCatchCopies,
    review,
    productDetailUrl,
    getLotteryInfo,
  };
};
